import React, { useEffect, useState } from 'react';
import { FileText, Plus, Download, Send, Trash2, Check, X, RefreshCw, Building2, User, Mail, Phone, Calendar, Eye } from 'lucide-react';
import { invoiceService, apartmentService } from '../../services/api';
import toast from 'react-hot-toast';

interface Invoice {
  id: number;
  invoice_number: string;
  apartment: {
    id: number;
    titre: string;
    adresse: string;
    ville: string;
  } | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  date_debut: string | null;
  date_fin: string | null;
  nb_nuits: number;
  description: string | null;
  montant: number;
  montant_total: number | null;
  invoice_type: 'total' | 'acompte';
  is_paid: boolean;
  sent_by_email: boolean;
  sent_to_space: boolean;
  sent_at: string | null;
  created_at: string;
  recipient: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
  } | null;
}

interface Apartment {
  id: number;
  titre: string;
  adresse: string;
  ville: string;
}

interface Locataire {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string | null;
}

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [locataires, setLocataires] = useState<Locataire[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Formulaire de création
  const [formData, setFormData] = useState({
    apartment_id: 0,
    client_name: '',
    client_email: '',
    client_phone: '',
    date_debut: '',
    date_fin: '',
    description: '',
    montant: 0,
    montant_total: 0,
    invoice_type: 'total' as 'total' | 'acompte',
    is_paid: false
  });

  // Formulaire d'envoi
  const [sendData, setSendData] = useState({
    send_email: true,
    send_to_space: false,
    recipient_id: 0,
    recipient_email: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, apartmentsRes, locatairesRes] = await Promise.all([
        invoiceService.getAll(),
        apartmentService.getMyApartments(),
        invoiceService.getLocataires()
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setApartments(apartmentsRes.data.apartments || []);
      setLocataires(locatairesRes.data.locataires || []);
    } catch (error: any) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.apartment_id || !formData.client_name || !formData.montant) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validation: date_fin >= date_debut
    if (formData.date_debut && formData.date_fin && formData.date_fin < formData.date_debut) {
      toast.error('La date de fin ne peut pas être antérieure à la date de début');
      return;
    }

    setSubmitting(true);
    try {
      await invoiceService.create({
        apartment_id: formData.apartment_id,
        client_name: formData.client_name,
        client_email: formData.client_email || undefined,
        client_phone: formData.client_phone || undefined,
        date_debut: formData.date_debut || undefined,
        date_fin: formData.date_fin || undefined,
        description: formData.description || undefined,
        montant: formData.montant,
        montant_total: formData.invoice_type === 'acompte' ? formData.montant_total : undefined,
        invoice_type: formData.invoice_type,
        is_paid: formData.is_paid
      } as any);
      toast.success('Facture créée avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      const response = await invoiceService.download(invoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    if (!sendData.send_email && !sendData.send_to_space) {
      toast.error('Veuillez choisir au moins une méthode d\'envoi');
      return;
    }

    if (sendData.send_email && !sendData.recipient_email && !sendData.recipient_id) {
      toast.error('Veuillez spécifier un email ou sélectionner un locataire');
      return;
    }

    setSubmitting(true);
    try {
      await invoiceService.send(selectedInvoice.id, {
        send_email: sendData.send_email,
        send_to_space: sendData.send_to_space,
        recipient_id: sendData.recipient_id || undefined,
        recipient_email: sendData.recipient_email || undefined
      });
      toast.success('Facture envoyée avec succès');
      setShowSendModal(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePaid = async (invoice: Invoice) => {
    try {
      await invoiceService.update(invoice.id, { is_paid: !invoice.is_paid });
      toast.success(invoice.is_paid ? 'Marquée comme non payée' : 'Marquée comme payée');
      fetchData();
    } catch (error: any) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (invoice: Invoice) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette facture ?')) return;
    
    try {
      await invoiceService.delete(invoice.id);
      toast.success('Facture supprimée');
      fetchData();
    } catch (error: any) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      apartment_id: apartments[0]?.id || 0,
      client_name: '',
      client_email: '',
      client_phone: '',
      date_debut: '',
      date_fin: '',
      description: '',
      montant: 0,
      montant_total: 0,
      invoice_type: 'total',
      is_paid: false
    });
  };

  const openSendModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setSendData({
      send_email: true,
      send_to_space: false,
      recipient_id: 0,
      recipient_email: invoice.client_email || ''
    });
    setShowSendModal(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Quand on sélectionne un locataire, remplir l'email
  const handleLocataireSelect = (locataireId: number) => {
    const locataire = locataires.find(l => l.id === locataireId);
    setSendData({
      ...sendData,
      recipient_id: locataireId,
      recipient_email: locataire?.email || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Factures</h1>
          <p className="text-neutral-600">Créez et envoyez des factures personnalisées</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="btn btn-outline flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvelle facture
          </button>
        </div>
      </div>

      {/* Liste des factures */}
      {invoices.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">Aucune facture créée</p>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="btn btn-primary mt-4"
          >
            Créer ma première facture
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="card p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                      #{invoice.invoice_number}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      invoice.invoice_type === 'acompte'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-neutral-100 text-neutral-800'
                    }`}>
                      {invoice.invoice_type === 'acompte' ? 'Acompte' : 'Total'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      invoice.is_paid 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {invoice.is_paid ? '✓ Payée' : '⏳ À payer'}
                    </span>
                    {invoice.sent_by_email && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        📧 Envoyée
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    <p className="flex items-center gap-2 font-medium">
                      <Building2 className="w-4 h-4 text-primary-500" />
                      {invoice.apartment?.titre || 'N/A'}
                    </p>
                    <p className="flex items-center gap-2 text-neutral-600">
                      <User className="w-4 h-4" />
                      {invoice.client_name}
                      {invoice.client_email && (
                        <span className="text-neutral-400">({invoice.client_email})</span>
                      )}
                    </p>
                    {invoice.date_debut && invoice.date_fin && (
                      <p className="flex items-center gap-2 text-neutral-500">
                        <Calendar className="w-4 h-4" />
                        {formatDate(invoice.date_debut)} → {formatDate(invoice.date_fin)}
                        {invoice.nb_nuits > 0 && (
                          <span>({invoice.nb_nuits} nuit{invoice.nb_nuits > 1 ? 's' : ''})</span>
                        )}
                      </p>
                    )}
                    <p className="font-semibold text-lg text-primary-600">
                      {formatPrice(invoice.montant)}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleDownload(invoice)}
                    className="btn btn-outline btn-sm flex items-center gap-1"
                    title="Télécharger PDF"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  <button
                    onClick={() => openSendModal(invoice)}
                    className="btn btn-outline btn-sm flex items-center gap-1"
                    title="Envoyer"
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                  <button
                    onClick={() => handleTogglePaid(invoice)}
                    className={`btn btn-sm flex items-center gap-1 ${
                      invoice.is_paid 
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={invoice.is_paid ? 'Marquer non payée' : 'Marquer payée'}
                  >
                    {invoice.is_paid ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    {invoice.is_paid ? 'Non payée' : 'Payée'}
                  </button>
                  <button
                    onClick={() => handleDelete(invoice)}
                    className="btn bg-red-100 text-red-700 hover:bg-red-200 btn-sm flex items-center gap-1"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-500" />
                Nouvelle facture
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateInvoice} className="p-4 space-y-4">
              {/* Appartement */}
              <div>
                <label className="block text-sm font-medium mb-1">Appartement *</label>
                <select
                  value={formData.apartment_id}
                  onChange={(e) => setFormData({ ...formData, apartment_id: Number(e.target.value) })}
                  className="input w-full"
                  required
                >
                  <option value="">Sélectionner un appartement</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.titre}</option>
                  ))}
                </select>
              </div>

              {/* Informations client */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Informations client
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom complet *</label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="input w-full"
                      placeholder="Nom du client"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                        className="input w-full"
                        placeholder="email@exemple.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={formData.client_phone}
                        onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                        className="input w-full"
                        placeholder="+221 77 000 00 00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Période */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Période (optionnel)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date début</label>
                    <input
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date fin</label>
                    <input
                      type="date"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Type de facture */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Type de facture</h3>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="invoice_type"
                      value="total"
                      checked={formData.invoice_type === 'total'}
                      onChange={() => setFormData({ ...formData, invoice_type: 'total' })}
                      className="w-4 h-4"
                    />
                    <span>Montant total</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="invoice_type"
                      value="acompte"
                      checked={formData.invoice_type === 'acompte'}
                      onChange={() => setFormData({ ...formData, invoice_type: 'acompte' })}
                      className="w-4 h-4"
                    />
                    <span>Acompte</span>
                  </label>
                </div>
              </div>

              {/* Montant et description */}
              <div className="border-t pt-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input w-full"
                      placeholder="Ex: Location appartement décembre 2024"
                    />
                  </div>
                  
                  {formData.invoice_type === 'acompte' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Montant total de la location (FCFA) *</label>
                      <input
                        type="number"
                        value={formData.montant_total || ''}
                        onChange={(e) => setFormData({ ...formData, montant_total: Number(e.target.value) })}
                        className="input w-full"
                        placeholder="0"
                        min="0"
                        required
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {formData.invoice_type === 'acompte' ? `Montant de l'acompte (FCFA) *` : 'Montant total (FCFA) *'}
                    </label>
                    <input
                      type="number"
                      value={formData.montant || ''}
                      onChange={(e) => setFormData({ ...formData, montant: Number(e.target.value) })}
                      className="input w-full"
                      placeholder="0"
                      min="0"
                      required
                    />
                    {formData.invoice_type === 'acompte' && formData.montant_total > 0 && formData.montant > 0 && (
                      <p className="text-sm text-neutral-500 mt-1">
                        Reste à payer au check-in : {new Intl.NumberFormat('fr-FR').format(formData.montant_total - formData.montant)} FCFA
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_paid"
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="is_paid" className="text-sm">Déjà payée</label>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-outline flex-1"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Créer la facture
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'envoi */}
      {showSendModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Send className="w-5 h-5 text-primary-500" />
                Envoyer la facture
              </h2>
              <button
                onClick={() => { setShowSendModal(false); setSelectedInvoice(null); }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSendInvoice} className="p-4 space-y-4">
              <div className="bg-neutral-50 p-3 rounded-lg">
                <p className="font-medium">Facture #{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-neutral-600">{formatPrice(selectedInvoice.montant)}</p>
              </div>

              {/* Méthodes d'envoi */}
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendData.send_email}
                    onChange={(e) => setSendData({ ...sendData, send_email: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Mail className="w-4 h-4" />
                  <span>Envoyer par email</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendData.send_to_space}
                    onChange={(e) => setSendData({ ...sendData, send_to_space: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <User className="w-4 h-4" />
                  <span>Envoyer dans l'espace locataire</span>
                </label>
              </div>

              {/* Destinataire */}
              {(sendData.send_email || sendData.send_to_space) && (
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Sélectionner un locataire</label>
                    <select
                      value={sendData.recipient_id}
                      onChange={(e) => handleLocataireSelect(Number(e.target.value))}
                      className="input w-full"
                    >
                      <option value="">-- Ou saisir un email --</option>
                      {locataires.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.prenom} {loc.nom} ({loc.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {sendData.send_email && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Email du destinataire</label>
                      <input
                        type="email"
                        value={sendData.recipient_email}
                        onChange={(e) => setSendData({ ...sendData, recipient_email: e.target.value })}
                        className="input w-full"
                        placeholder="email@exemple.com"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowSendModal(false); setSelectedInvoice(null); }}
                  className="btn btn-outline flex-1"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
